import React from "react";
import { cn } from "@/shared/lib/cn";
import { Skeleton } from "./skeleton";

interface TableSkeletonProps {
    columns?: number;
    rows?: number;
    /** Largura relativa de cada coluna, ex.: ["30%", "20%", "20%", "30%"]. */
    columnWidths?: string[];
    /** Avatar circular na primeira coluna — só para listas de pessoas. */
    withAvatar?: boolean;
    /** Coluna final estreita para os botões de ação da linha. */
    withActions?: boolean;
    className?: string;
}

export function TableSkeleton({
    columns = 4,
    rows = 6,
    columnWidths,
    withAvatar = false,
    withActions = false,
    className,
}: TableSkeletonProps) {
    const widthOf = (index: number) =>
        columnWidths?.[index] ?? `${100 / columns}%`;

    // O corpo precisa somar `columns`: avatar e ações já ocupam uma célula cada,
    // e o bloco de nome ocupa outra.
    const fillerCells = Math.max(
        columns - (withAvatar ? 1 : 0) - 1 - (withActions ? 1 : 0),
        0,
    );

    return (
        <div
            className={cn(
                "w-full overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-zinc-700 dark:bg-night_surface",
                className,
            )}
            role="status"
            aria-busy="true"
            aria-live="polite"
        >
            <span className="sr-only">Carregando dados da tabela</span>

            <div className="flex h-12 items-center gap-4 border-b border-light_gray bg-gray-50 px-6 dark:border-zinc-700 dark:bg-night_surface">
                {Array.from({ length: columns }).map((_, index) => (
                    <Skeleton
                        key={index}
                        className="h-4"
                        style={{ width: widthOf(index) }}
                    />
                ))}
            </div>

            {Array.from({ length: rows }).map((_, rowIndex) => (
                <div
                    key={rowIndex}
                    className="flex h-16 items-center gap-4 border-b border-light_gray px-6 last:border-0 dark:border-zinc-700"
                >
                    {withAvatar && (
                        <Skeleton variant="circle" className="h-10 w-10 shrink-0" />
                    )}

                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-2 w-1/4 opacity-60" />
                    </div>

                    {Array.from({ length: fillerCells }).map((_, colIndex) => (
                        <Skeleton key={colIndex} className="h-4 w-20" />
                    ))}

                    {withActions && <Skeleton className="mx-2 h-8 w-8 shrink-0" />}
                </div>
            ))}
        </div>
    );
}
